import { motion } from 'framer-motion';

const stages = ['Intent', 'Execution', 'Evidence', 'Review', 'Governance', 'Improve'];

/**
 * Governed AI-work pipeline — responsive snake layout.
 * Desktop: single horizontal row.
 * Mobile: 2 rows of 3 with a U-bend connector.
 *
 *   1 ─── 2 ─── 3
 *              │
 *              │
 *   4 ─── 5 ─── 6
 */
export function WorkPipelineFlow() {
  return (
    <div className="w-full">
      {/* Desktop */}
      <div className="hidden sm:block">
        <div className="relative flex items-start justify-between">
          {/* base track + signal in same SVG (same approach as mobile) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {/* base track */}
            <line x1="0" y1="25" x2="100" y2="25" stroke="rgba(34,211,238,0.2)" strokeWidth="1" />
            {/* travelling signal */}
            <motion.path
              d="M 0 25 L 100 25"
              fill="none"
              stroke="#22d3ee"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="20 80"
              initial={{ strokeDashoffset: 0 }}
              animate={{ strokeDashoffset: -100 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
          </svg>
          {stages.map((s, i) => (
            <StageCircle key={s} label={s} index={i} />
          ))}
        </div>
      </div>

      {/* Mobile snake layout */}
      <MobileSnakeFlow />
    </div>
  );
}

/* ───────────────────────────────────────────────
   Mobile snake layout – SVG overlay for continuous path
──────────────────────────────────────────────── */
function MobileSnakeFlow() {
  const firstHalf = stages.slice(0, 3);
  const secondHalf = stages.slice(3);

  return (
    <div className="sm:hidden relative w-full pb-4">
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-0"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {/* row 1 track */}
        <line x1="2" y1="10" x2="98" y2="10" stroke="rgba(34,211,238,0.2)" strokeWidth="1" />
        {/* row 2 track */}
        <line x1="2" y1="72" x2="98" y2="72" stroke="rgba(34,211,238,0.2)" strokeWidth="1" />
        {/* full snake path background */}
        <path
          d="M 2 10 L 98 10 L 98 40 L 2 40 L 2 72 L 98 72"
          fill="none"
          stroke="rgba(34,211,238,0.2)"
          strokeWidth="1"
        />
        {/* travelling signal – thin bright dash sliding along the snake */}
        <motion.path
          d="M 2 10 L 98 10 L 98 40 L 2 40 L 2 72 L 98 72"
          fill="none"
          stroke="#22d3ee"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="20 380"
          initial={{ strokeDashoffset: 0 }}
          animate={{ strokeDashoffset: -400 }}
          transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
        />
      </svg>

      <div className="flex flex-col gap-16 relative z-10">
        <div className="relative flex items-start justify-between w-full">
          {firstHalf.map((s, i) => (
            <StageCircle key={s} label={s} index={i} />
          ))}
        </div>
        <div className="relative flex items-start justify-between w-full">
          {secondHalf.map((s, i) => (
            <StageCircle key={s} label={s} index={i + 3} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── shared stage circle ── */
function StageCircle({ label, index }: { label: string; index: number }) {
  return (
    <motion.div
      className="relative z-10 flex w-16 sm:w-24 flex-col items-center gap-3"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12 }}
    >
      <motion.span
        className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border border-cyan-400/40 bg-[#0b1030] text-cyan-300 text-xs sm:text-sm"
        animate={{ boxShadow: ['0 0 0px rgba(34,211,238,0)', '0 0 14px rgba(34,211,238,0.6)', '0 0 0px rgba(34,211,238,0)'] }}
        transition={{ duration: 3, repeat: Infinity, delay: index * 0.6 }}
      >
        {index + 1}
      </motion.span>
      <span className="text-center text-[10px] sm:text-sm text-cyan-100/80">{label}</span>
    </motion.div>
  );
}
